import { Fragment, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { ChevronDown, ChevronRight, Rows3 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TableSkeleton } from '@/components/ui/skeleton'
import { useTransactions } from '@/hooks/useTransactions'
import { useCategories, buildTree } from '@/hooks/useCategories'
import { useTranslateCategory, useCategorySortKey } from '@/lib/categoryDisplay'
import { faturaMonthOf } from '@/lib/finance'
import { formatCurrency } from '@/lib/currency'
import { useDateFnsLocale } from '@/i18n/dateLocale'
import { useHousehold } from '@/context/HouseholdContext'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'

// Últimos N meses (mês corrente incluído), em ordem crescente ('YYYY-MM').
function lastMonthKeys(n: number): string[] {
  const now = new Date()
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (n - 1 - i), 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })
}

const NONE = '__none__'

interface CatRow {
  id: string
  name: string
  icon: string | null
  byMonth: Map<string, { net: number; count: number }>
  total: number
  count: number
}

export function ComparisonPage() {
  const { t } = useT()
  const tc = useTranslateCategory()
  const sortKey = useCategorySortKey()
  const { baseCurrency } = useHousehold()
  const locale = useDateFnsLocale()

  const [period, setPeriod] = useState('6')
  const PERIODS = [
    { v: '3', label: t('rep.periodMonths', { n: 3 }) },
    { v: '6', label: t('rep.periodMonths', { n: 6 }) },
    { v: '12', label: t('rep.periodMonths', { n: 12 }) },
    { v: '24', label: t('rep.periodMonths', { n: 24 }) },
  ]
  const months = useMemo(() => lastMonthKeys(Number(period)), [period])

  const { data: categories } = useCategories()
  const { data: allTxs } = useTransactions({})

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded((s) => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })

  // Lançado por CATEGORIA x MÊS — régua de fatura (faturaMonthOf), a mesma do
  // Orçamento/Relatórios: compra de cartão cai no mês em que a fatura vence.
  // Receita soma +, despesa soma − (mesma categoria pode ter os dois, ex.:
  // investimento com ganho e perda), então o resultado já sai líquido e com
  // o sinal certo sem precisar saber o `kind` da categoria.
  const buckets = useMemo(() => {
    const from = months[0]
    const to = months[months.length - 1]
    const map = new Map<string, Map<string, { net: number; count: number }>>()
    for (const tx of allTxs ?? []) {
      if (tx.transfer_group_id || tx.amount_base == null) continue
      if (tx.kind !== 'income' && tx.kind !== 'expense') continue
      const mk = faturaMonthOf(tx)
      if (mk < from || mk > to) continue
      const key = tx.category_id ?? NONE
      const byMonth = map.get(key) ?? new Map<string, { net: number; count: number }>()
      const cur = byMonth.get(mk) ?? { net: 0, count: 0 }
      cur.net += tx.kind === 'income' ? Number(tx.amount_base) : -Number(tx.amount_base)
      cur.count += 1
      byMonth.set(mk, cur)
      map.set(key, byMonth)
    }
    return map
  }, [allTxs, months])

  function rowOf(ids: string[], name: string, icon: string | null): CatRow {
    const byMonth = new Map<string, { net: number; count: number }>()
    let total = 0
    let count = 0
    for (const mk of months) {
      let net = 0
      let c = 0
      for (const id of ids) {
        const cell = buckets.get(id)?.get(mk)
        if (!cell) continue
        net += cell.net
        c += cell.count
      }
      if (c > 0) byMonth.set(mk, { net, count: c })
      total += net
      count += c
    }
    return { id: ids.join('|'), name, icon, byMonth, total, count }
  }

  const { parentRows, childRowsOf, noneRow } = useMemo(() => {
    const tree = buildTree(categories ?? [])
      .sort((a, b) => sortKey(a.parent.name).localeCompare(sortKey(b.parent.name)))
      // Receitas primeiro, depois despesas/investimento — igual ao resto do app.
      .sort((a, b) => (a.parent.kind === 'income' ? 0 : 1) - (b.parent.kind === 'income' ? 0 : 1))

    const parents: CatRow[] = []
    const childMap = new Map<string, CatRow[]>()
    for (const g of tree) {
      const ids = [g.parent.id, ...g.children.map((c) => c.id)]
      const row = rowOf(ids, tc(g.parent.name), g.parent.icon)
      if (row.count === 0) continue
      parents.push(row)
      if (g.children.length > 0) {
        const kids = g.children
          .map((c) => rowOf([c.id], tc(c.name), c.icon))
          .filter((r) => r.count > 0)
          .sort((a, b) => a.name.localeCompare(b.name))
        if (kids.length > 0) childMap.set(g.parent.id, kids)
      }
    }
    const none = rowOf([NONE], t('cmp.noCategory'), null)
    return { parentRows: parents, childRowsOf: childMap, noneRow: none.count > 0 ? none : null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, buckets, months, tc, sortKey, t])

  const allRows = noneRow ? [...parentRows, noneRow] : parentRows

  // Linhas TOTAL e MÉDIA do rodapé — soma/média de todas as categorias por mês.
  const footer = useMemo(() => {
    const totalByMonth = new Map<string, number>()
    for (const r of allRows) for (const [mk, cell] of r.byMonth) totalByMonth.set(mk, (totalByMonth.get(mk) ?? 0) + cell.net)
    const grandTotal = [...totalByMonth.values()].reduce((s, v) => s + v, 0)
    const monthsWithData = totalByMonth.size
    return { totalByMonth, grandTotal, avg: monthsWithData > 0 ? grandTotal / monthsWithData : 0 }
  }, [allRows])

  const fmt = (v: number) => formatCurrency(v, baseCurrency)
  const monthHead = (mk: string) => {
    const [y, m] = mk.split('-').map(Number)
    return format(new Date(y, m - 1, 1), 'MMM yyyy', { locale }).toUpperCase()
  }
  const cellClass = (v: number) => cn('text-right tabular-nums whitespace-nowrap', v > 0.005 && 'text-success')

  const loading = !allTxs || !categories

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title={t('cmp.title')}
        subtitle={t('cmp.subtitle')}
        actions={
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.v} value={p.v}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={8} cols={months.length + 4} />
          ) : allRows.length === 0 ? (
            <EmptyState icon={Rows3} title={t('cmp.emptyTitle')} description={t('cmp.emptyDesc')} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 z-10 bg-inherit">{t('cmp.thCategory')}</TableHead>
                    {months.map((mk) => (
                      <TableHead key={mk} className="text-right whitespace-nowrap">
                        {monthHead(mk)}
                      </TableHead>
                    ))}
                    <TableHead className="text-right">{t('cmp.thTotal')}</TableHead>
                    <TableHead className="text-right">{t('cmp.thAverage')}</TableHead>
                    <TableHead className="text-right">{t('cmp.thCount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allRows.map((r) => {
                    const kids = childRowsOf.get(r.id)
                    const isOpen = expanded.has(r.id)
                    const rowAvg = r.byMonth.size > 0 ? r.total / r.byMonth.size : 0
                    return (
                      <Fragment key={r.id}>
                        <TableRow>
                          <TableCell className="sticky left-0 z-10 bg-inherit font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              {kids ? (
                                <button
                                  onClick={() => toggle(r.id)}
                                  className="text-muted-foreground hover:text-foreground -ml-1"
                                  aria-label={isOpen ? t('cmp.collapse') : t('cmp.expand')}
                                >
                                  {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                                </button>
                              ) : (
                                <span className="inline-block w-4" />
                              )}
                              {r.icon && <span>{r.icon}</span>}
                              {r.name}
                            </span>
                          </TableCell>
                          {months.map((mk) => {
                            const cell = r.byMonth.get(mk)
                            return (
                              <TableCell key={mk} className={cellClass(cell?.net ?? 0)}>
                                {cell ? fmt(cell.net) : <span className="text-muted-foreground">—</span>}
                              </TableCell>
                            )
                          })}
                          <TableCell className={cn('font-medium', cellClass(r.total))}>{fmt(r.total)}</TableCell>
                          <TableCell className={cellClass(rowAvg)}>{fmt(rowAvg)}</TableCell>
                          <TableCell className="text-muted-foreground text-right tabular-nums">{r.count}</TableCell>
                        </TableRow>
                        {isOpen &&
                          kids?.map((k) => {
                            const kAvg = k.byMonth.size > 0 ? k.total / k.byMonth.size : 0
                            return (
                              <TableRow key={k.id} className="bg-accent/20">
                                <TableCell className="sticky left-0 z-10 bg-inherit pl-9 text-sm">
                                  <span className="inline-flex items-center gap-1.5">
                                    {k.icon && <span>{k.icon}</span>}
                                    {k.name}
                                  </span>
                                </TableCell>
                                {months.map((mk) => {
                                  const cell = k.byMonth.get(mk)
                                  return (
                                    <TableCell key={mk} className={cellClass(cell?.net ?? 0)}>
                                      {cell ? fmt(cell.net) : <span className="text-muted-foreground">—</span>}
                                    </TableCell>
                                  )
                                })}
                                <TableCell className={cellClass(k.total)}>{fmt(k.total)}</TableCell>
                                <TableCell className={cellClass(kAvg)}>{fmt(kAvg)}</TableCell>
                                <TableCell className="text-muted-foreground text-right tabular-nums">{k.count}</TableCell>
                              </TableRow>
                            )
                          })}
                      </Fragment>
                    )
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow className="font-semibold">
                    <TableCell className="sticky left-0 z-10 bg-inherit">{t('cmp.rowTotal')}</TableCell>
                    {months.map((mk) => (
                      <TableCell key={mk} className={cellClass(footer.totalByMonth.get(mk) ?? 0)}>
                        {fmt(footer.totalByMonth.get(mk) ?? 0)}
                      </TableCell>
                    ))}
                    <TableCell className={cellClass(footer.grandTotal)}>{fmt(footer.grandTotal)}</TableCell>
                    <TableCell className={cellClass(footer.avg)}>{fmt(footer.avg)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
